import React, { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Badge } from 'antd';
import { ArrowLeftOutlined, WarningOutlined } from '@ant-design/icons';
import DismissibleAlert from '../../components/DismissibleAlert';
import { useAuth } from '../../assets/utils/useAuth';
import { usePermissions } from '../../assets/utils/usePermissions';
import DashboardSection from '../dashboard/components/DashboardSection';
import EnrollmentConflictsPanel from './components/EnrollmentConflictsPanel';
import useEnrollmentConflictsCount from './hooks/useEnrollmentConflictsCount';
import { canManageEnrollmentConflicts } from './enrollmentConflictHelpers';
import './orgs-dashboard.css';

export const ENROLLMENT_HOLDS_CONTEXT_BANNER_KEY = 'orgs-enrollment-holds-context-v1';

/**
 * Cross-company enrollment holds — still inside the Organizations product.
 * Primary path: pause banner on the company upload/session that created the hold.
 */
export default function EnrollmentHoldsQueue() {
  const { user } = useAuth();
  const { hasAnyPermission } = usePermissions();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const canReview = useMemo(
    () => canManageEnrollmentConflicts(user, hasAnyPermission),
    [user, hasAnyPermission],
  );
  const {
    open: openCount,
    pausedSessions,
    refresh,
  } = useEnrollmentConflictsCount(Boolean(user && canReview));

  const sessionHint = searchParams.get('session_id');
  const socialHint = searchParams.get('social_no');

  return (
    <div className="orgs-dashboard">
      <div className="dashboard-page-stack">
        <header className="dash-masthead">
          <div className="dash-masthead__lead">
            <p className="org-detail-breadcrumb">
              <Button
                type="link"
                className="org-detail-breadcrumb__link"
                onClick={() => navigate('/corporations')}
              >
                Organizations
              </Button>
              <span aria-hidden>/</span>
              <span>Enrollment holds</span>
            </p>
            <h1 className="dash-masthead__title">
              Enrollment holds across companies
              {openCount > 0 ? (
                <Badge
                  count={openCount}
                  overflowCount={99}
                  style={{ marginLeft: 12, verticalAlign: 'middle' }}
                />
              ) : null}
            </h1>
            <p className="dash-masthead__meta">
              {pausedSessions > 0
                ? `${pausedSessions} upload${pausedSessions === 1 ? '' : 's'} still have conflicts — clean rows may already be applied.`
                : 'Resolve remaining conflicts so deals / staff create_new rows can finish.'}
              {sessionHint || socialHint ? (
                <>
                  {' '}
                  Showing
                  {sessionHint ? ` session #${sessionHint}` : ''}
                  {socialHint ? ` · ${socialHint}` : ''}
                  .
                </>
              ) : null}
            </p>
          </div>
          <div className="dash-masthead__actions">
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/corporations')}>
              Back to directory
            </Button>
          </div>
        </header>

        <DismissibleAlert
          bannerKey={ENROLLMENT_HOLDS_CONTEXT_BANNER_KEY}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="You are still in Organizations"
          description={
            sessionHint || socialHint
              ? 'Filters came from a partial upload on a company. Clear them only if you need the full cross-company backlog.'
              : 'Best path: open the company banner after upload for the session summary (added / dismissed / needs resolution). This list is the cross-company backlog when you need to clear several holds.'
          }
        />

        <DashboardSection
          id="orgs-enrollment-holds-queue"
          icon={<WarningOutlined />}
          title="Open holds"
          subtitle="Tiered apply — clean and identical rows are handled at upload; only real conflicts stay here"
          collapsible={false}
          variant="content"
        >
          <EnrollmentConflictsPanel onCountsChange={refresh} />
        </DashboardSection>
      </div>
    </div>
  );
}
